import axios from 'axios';

const PREVIEW_HOST_SUFFIX = '.preview.emergentagent.com';
const CACHE_PREFIX = 'link-public-data-v3:';
const CACHE_VERSION_KEY = `${CACHE_PREFIX}version`;

const cleanOrigin = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'undefined' || raw === 'null') return '';
  return raw.replace(/\/+$/, '');
};

const resolveBackendOrigin = () => {
  const configured = cleanOrigin(process.env.REACT_APP_BACKEND_URL);

  if (typeof window === 'undefined') return configured;
  if (!configured) return '';

  try {
    const configuredUrl = new URL(configured, window.location.origin);
    const runtimeHost = window.location.hostname.toLowerCase();
    const configuredHost = configuredUrl.hostname.toLowerCase();

    // A production/custom-domain build must never keep talking to the Preview
    // backend because of an environment variable baked into an older build.
    if (
      configuredHost.endsWith(PREVIEW_HOST_SUFFIX) &&
      !runtimeHost.endsWith(PREVIEW_HOST_SUFFIX)
    ) {
      return '';
    }
  } catch {
    // Keep the configured value. Axios will expose a useful request error if it
    // is malformed instead of silently switching the content source.
  }

  return configured;
};

export const BACKEND_ORIGIN = resolveBackendOrigin();
export const API_ROOT = `${BACKEND_ORIGIN}/api`;

export const publicApi = axios.create({
  baseURL: `${API_ROOT}/public`,
  timeout: 20_000,
  headers: {
    Accept: 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
  },
});

publicApi.interceptors.request.use((config) => ({
  ...config,
  params: {
    ...(config.params || {}),
    _ts: Date.now(),
  },
}));

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Read public data with small retries for transient proxy/backend failures.
 * A failed request never authorizes the UI to resurrect bundled preview data.
 */
export async function getPublic(path, config = {}) {
  let lastError;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await publicApi.get(path, config);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const retryable = !status || status === 408 || status === 429 || status >= 500;

      if (!retryable || attempt === 2) break;
      await wait(350 * (attempt + 1));
    }
  }

  throw lastError;
}

export const toAssetUrl = (path) => {
  if (!path) return path;
  const value = String(path);

  if (value.startsWith('/api/uploads/')) {
    return `${BACKEND_ORIGIN}${value}`;
  }

  // Repair old database records that accidentally stored an absolute Preview
  // media/static URL. On the deployed/custom domain, serve the same pathname
  // from the current runtime instead of crossing back into Preview.
  if (/^https?:\/\//i.test(value) && typeof window !== 'undefined') {
    try {
      const url = new URL(value);
      const isPreviewAsset = url.hostname
        .toLowerCase()
        .endsWith(PREVIEW_HOST_SUFFIX);
      const runtimeIsPreview = window.location.hostname
        .toLowerCase()
        .endsWith(PREVIEW_HOST_SUFFIX);
      const isPortablePath =
        url.pathname.startsWith('/api/uploads/') ||
        url.pathname.startsWith('/assets/');

      if (isPreviewAsset && !runtimeIsPreview && isPortablePath) {
        return `${BACKEND_ORIGIN}${url.pathname}${url.search}`;
      }
    } catch {
      return value;
    }
  }

  return value;
};

export function readPublicCache(key) {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.value ?? null;
  } catch {
    return null;
  }
}

export function writePublicCache(key, value) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(
      `${CACHE_PREFIX}${key}`,
      JSON.stringify({ savedAt: Date.now(), value }),
    );
  } catch {
    // Storage can be disabled by the browser; the in-memory cache still works.
  }
}


export function getPublicCacheVersion() {
  if (typeof window === 'undefined') return 0;
  try {
    return Number(window.sessionStorage.getItem(CACHE_VERSION_KEY) || 0);
  } catch {
    return 0;
  }
}

/** Clear stale public snapshots after an administrator mutation. */
export function invalidatePublicData() {
  if (typeof window === 'undefined') return;

  try {
    const nextVersion = getPublicCacheVersion() + 1;
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(CACHE_PREFIX) && key !== CACHE_VERSION_KEY)
      .forEach((key) => window.sessionStorage.removeItem(key));
    window.sessionStorage.setItem(CACHE_VERSION_KEY, String(nextVersion));
    window.dispatchEvent(
      new CustomEvent('link:public-data-invalidated', {
        detail: { version: nextVersion },
      }),
    );
  } catch {
    // The next public GET still revalidates directly against the API.
  }
}
